/**
 * isiXhosa: Signing in and getting started
 *
 * What this file covers: Sign in, sign up, password reset, two-step sign-in, email verification, the data
 * processing agreement and the first-run onboarding.
 *
 * Tone: Warm, calm and reassuring, in very plain words: this is a business owner's first impression. Security
 * messages stay exact (a wrong code is wrong, an expired link is expired). The agreement text is legal:
 * translate it faithfully, never shorten or soften it.
 *
 * isiXhosa: Everyday isiXhosa as used in South African business. Keys not translated yet fall back to English,
 * so a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const signIn: Catalog = {
  'auth.common.password': 'Iphaswedi',
  'auth.common.codeMismatch': 'Loo khowudi ayihambelani.',
  'auth.common.backToSignIn': 'Buyela ekungeneni',
  'auth.login.newHere': 'Umtsha apha?',
  'auth.login.createAccount': 'Yenza i-akhawunti',
  'auth.login.forgotPassword': 'Ulibele iphaswedi?',
  'auth.login.failed': 'Ukungena kusilele.',
  'auth.login.twofa.form.code': 'Ikhowudi yoqinisekiso',
  'auth.login.twofa.verify': 'Qinisekisa',
  'auth.signup.title': 'Yenza i-akhawunti yakho',
  'auth.signup.haveAccount': 'Sele unayo i-akhawunti?',
  'auth.signup.form.orgName': 'Igama lombutho',
  'auth.signup.form.workEmail': 'I-imeyile yomsebenzi',
  'auth.signup.submit': 'Yenza i-akhawunti',
  'auth.signup.failed': 'Ukubhalisa kusilele.',
  'auth.forgot.title': 'Seta kwakhona iphaswedi yakho',
  'auth.forgot.submit': 'Thumela ikhonkco lokuseta kwakhona',
  'auth.forgot.sent.title': 'Jonga ibhokisi yakho yokungena',
  'auth.reset.title': 'Khetha iphaswedi entsha',
  'auth.reset.form.newPassword': 'Iphaswedi entsha',
  'auth.reset.form.confirmPassword': 'Qinisekisa iphaswedi',
  'auth.reset.submit': 'Seta kwakhona iphaswedi',
  'auth.reset.errors.tooShort': 'Iphaswedi mayibe noonobumba abasi-8 ubuncinane.',
  'auth.reset.errors.mismatch': 'Iiphaswedi azifani.',
  'auth.reset.failed': 'Ayikwazanga ukuseta kwakhona iphaswedi.',
  'auth.reset.toast.doneTitle': 'Iphaswedi isetwe kwakhona',
  'auth.verify.title': 'Qinisekisa i-imeyile yakho',
  'auth.verify.form.code': 'Ikhowudi yamanani amathandathu',
  'auth.verify.submit': 'Qinisekisa i-imeyile',
  'auth.verify.resend': 'Thumela ikhowudi kwakhona',
  'auth.verify.backToDashboard': 'Buyela kwideshibhodi',
  'auth.verify.toast.done': 'I-imeyile yakho iqinisekisiwe.',
  'auth.verify.toast.resentTitle': 'Ikhowudi ithunyelwe',
  'auth.verify.toast.resendFailed': 'Ayikwazanga ukuthumela kwakhona.',
  'auth.verify.toast.resendFailedTitle': 'Ukuthumela kwakhona kusilele',
  'auth.mfa.title': 'Ukungena ngamanyathelo amabini',
  'auth.mfa.goToSignIn': 'Yiya ekungeneni',
  'onboarding.title': 'Wamkelekile ku-RepletoryReach',
  'onboarding.skipSetup': 'Tsiba useto',
  'onboarding.steps.verify.label': 'Qinisekisa i-imeyile',
  'onboarding.steps.verify.title': 'Qinisekisa i-imeyile yakho',
  'onboarding.steps.verify.form.code': 'Ikhowudi yoqinisekiso',
  'onboarding.steps.verify.resend': 'Thumela ikhowudi kwakhona',
  'onboarding.steps.verify.skip': 'Tsiba okwangoku',
  'onboarding.steps.verify.confirm': 'Qinisekisa',
  'onboarding.steps.region.label': 'Ummandla',
  'onboarding.steps.region.title': 'Usebenza phi?',
  'onboarding.steps.region.form.country': 'Ilizwe',
  'onboarding.steps.region.country.za': 'UMzantsi Afrika (ZAR)',
  'onboarding.steps.region.country.ng': 'INigeria (NGN)',
  'onboarding.steps.region.country.ke': 'IKenya (KES)',
  'onboarding.steps.name.label': 'Igama lakho',
  'onboarding.steps.name.title': 'Sikubize ngubani?',
  'onboarding.steps.name.form.displayName': 'Igama elibonakalayo',
  'onboarding.steps.number.label': 'Inani le-WhatsApp',
  'onboarding.steps.number.title': 'Qhoboshela inani le-WhatsApp',
  'onboarding.steps.number.openNumbers': 'Vula Amanani',
  'onboarding.steps.done.label': 'Kugqityiwe',
  'onboarding.steps.done.title': 'Sele ulungile',
  'onboarding.steps.done.goToDashboard': 'Yiya kwideshibhodi',
  'onboarding.toast.emailVerified': 'I-imeyile iqinisekisiwe.',
  'onboarding.toast.emailVerifiedTitle': 'Ukungeniswa',
  'onboarding.toast.codeMismatch': 'Ikhowudi ayihambelani.',
  'onboarding.toast.codeMismatchTitle': 'Uqinisekiso',
  'onboarding.toast.saveFailed': 'Ayikwazanga ukugcina.',
  'onboarding.toast.profileFailedTitle': 'Iprofayile',
  'onboarding.stepOf': 'Inyathelo {{step}} kwangu-{{total}}',
  'onboarding.stepLabel': 'Inyathelo',
  'auth.social.or': 'okanye',
  'auth.social.continueWith': 'Qhubeka nge-{{provider}}',
  'auth.social.provider': 'umboneleli wakho',
  'auth.social.hint': 'Sifumana kuphela igama lakho kunye nedilesi ye-imeyile, kwaye emva kokuba uvumile.',
  'auth.callback.signingIn': 'Siyakungenisa…',
  'auth.callback.failedTitle': 'Ukungena akugqibanga',
  'auth.callback.failedLead': 'Akukho nto itshintshileyo kwiakhawunti yakho. Zama kwakhona, okanye ungene ngedilesi yakho ye-imeyile nephasiwedi.',
  'auth.callback.backToSignIn': 'Buyela ekungeneni'
};

export default signIn;
